import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import SelectField from '../common/SelectField.js';
import { shipmentReturnService } from "../../services/shipmentReturnService"
import { shipmentService } from "../../services/shipmentService"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Alert from "../common/Alert"
import { Settings, ArrowLeft  } from "lucide-react";
import Button from '../common/Button.js';
import { validators, useValidation, ValidationMessage } from "../common/Validation.js";
import { useTranslation } from 'react-i18next';


const AddReturn = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { errors, validate, validateField } = useValidation()

  const validationRules = {
    shipment_id:       (v) => validators.required(v, 'Shipment ID'),
    reason_for_return: (v) => validators.select(v, 'reason for return'),
  }

  const [loading, setLoading] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const [shipmentDetails, setShipmentDetails] = useState(null)
  const [returnReasons, setReturnReasons] = useState([])
  const [formData, setFormData] = useState({
    shipment_id: "",
    reason_for_return: "",
  })

  useEffect(() => {
    const fetchReturnReasons = async () => {
      try {
        const response = await shipmentReturnService.getReturnReasons()
        if (response.data.success) {
          setReturnReasons(response.data.data)
        }
      } catch (error) {
        console.error("Error fetching return reasons:", error)
        // Set default reasons if API fails
        setReturnReasons([
          { value: 'wrong_address', label: 'Wrong Address' },
          { value: 'receiver_refused', label: 'Receiver Refused' },
          { value: 'damaged_product', label: 'Damaged Product' },
          { value: 'incomplete_shipment', label: 'Incomplete Shipment' },
          { value: 'delivery_delay', label: 'Delivery Delay' },
          { value: 'customer_request', label: 'Customer Request' },
          { value: 'incorrect_item', label: 'Incorrect Item' },
          { value: 'no_recipient_available', label: 'No Recipient Available' },
          { value: 'other', label: 'Other' }
        ])
      }
    }
    fetchReturnReasons()
  }, [])

  const handleShipmentSearch = async (e) => {
    const shipmentId = e.target.value
    setFormData({ ...formData, shipment_id: shipmentId })
    setShipmentDetails(null)
    validateField('shipment_id', shipmentId, validationRules.shipment_id)

    if (shipmentId.trim().length === 0) {
      return
    }

    try {
      setSearchLoading(true)
      const response = await shipmentService.getShipment(shipmentId)

      const shipment = response.data.shipment || response.data.data

      if (shipment) {
        // Check if shipment is already returned or closed
        if (
          shipment.status === "Returned" ||
          shipment.status === "RETURNED" ||
          shipment.status === "close" ||
          shipment.status === "closed"
        ) {
          Alert.error("This shipment is already marked as returned or closed")
          setShipmentDetails(null)
          return
        }

        setShipmentDetails(shipment)
      } else {
        setShipmentDetails(null)
        Alert.error("Shipment not found")
      }
    } catch (error) {
      setShipmentDetails(null)
      console.error("Shipment search error:", error)

      if (error.response?.status === 404) {
        Alert.error("Shipment not found")
      } else if (error.response?.data?.error) {
        Alert.error(error.response.data.error)
      } else {
        Alert.error("Error searching for shipment. Please try again.")
      }
    } finally {
      setSearchLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })

    if (validationRules[name]) {
      validateField(name, value, validationRules[name])
    }
  }

  const validateForm = () => {
    if (!validate(formData, validationRules)) return false

    if (!shipmentDetails) {
      Alert.error("Please search and select a valid shipment")
      return false
    }

    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    const confirmed = await Alert.confirm(
      `Submit return request for shipment ${formData.shipment_id}?`,
      "Confirm Return Request",
    )

    if (!confirmed) return

    try {
      setLoading(true)

      const submitData = {
        shipment_id: formData.shipment_id.trim(),
        reason_for_return: formData.reason_for_return,
      }

      await shipmentReturnService.createReturn(submitData)

      Alert.success("Return request created successfully!")
      navigate("/returns")
    } catch (error) {
      console.error("Error creating return:", error)

      if (error.response?.data?.error) {
        Alert.error(error.response.data.error)
      } else if (error.response?.status === 400) {
        Alert.error("Invalid request. Please check all fields and try again.")
      } else if (error.response?.status === 404) {
        Alert.error("Shipment not found or has already been returned.")
      } else {
        Alert.error("Failed to create return request. Please try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <DashboardLayout>
              <div className="max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="mb-6">
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mb-4 sm:mb-6">
                    <div>
                      <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">Request Shipment Return</h1>
                    </div>
                    <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate("/shipments")}>
                      Back to Returns
                    </Button>
                  </div>
                </div>

                {/* Shipment Lookup Card */}
                <div className="bg-white rounded-xl p-4 sm:p-6 mb-6">
                  <div className="w-full mb-4">
                    <h2 className="w-full text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Shipment Selection
                    </h2>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Shipment ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="shipment_id"
                      value={formData.shipment_id}
                      onChange={handleShipmentSearch}
                      placeholder="Enter shipment ID to search (e.g., 123456)"
                      className={`w-full px-4 py-3 text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all font-mono ${errors.shipment_id ? 'border-red-400' : 'border-gray-300'}`}
                      required
                      disabled={loading}
                    />
                    <ValidationMessage error={errors.shipment_id} />
                    {searchLoading && (
                      <div className="mt-2 flex items-center gap-2 text-orange-600 text-sm">
                        <div className="animate-spin rounded-full h-4 w-4 border border-orange-600 border-t-transparent"></div>
                        <span>Searching for shipment...</span>
                      </div>
                    )}
                  </div>

                  {/* Shipment Details Display */}
                  {shipmentDetails && (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 p-4 sm:p-6 rounded-xl">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="bg-green-500 rounded-full p-1">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <h3 className="text-lg font-bold text-green-800">Shipment Found</h3>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-4 bg-white p-4 sm:p-5 rounded-xl border border-gray-200">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Shipment ID</p>
                          <p className="text-base sm:text-lg font-bold text-orange-600 font-mono">
                            #{shipmentDetails.shipment_id}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Status</p>
                          <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                            {shipmentDetails.status}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Sender Name</p>
                          <p className="text-gray-900 font-semibold">{shipmentDetails.sender_name}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Receiver Name</p>
                          <p className="text-gray-900 font-semibold">{shipmentDetails.receiver_name}</p>
                        </div>
                        <div className="sm:col-span-2">
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Delivery Address</p>
                          <p className="text-gray-900 text-sm">{shipmentDetails.receiver_address}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Total Amount</p>
                          <p className="text-base sm:text-lg font-bold text-gray-900">
                            ₹{shipmentDetails.total_amount?.toFixed(2) || "0.00"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Payment Mode</p>
                          <p className="text-gray-900 font-semibold">{shipmentDetails.mode_of_payment || "N/A"}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Return Reason Form */}
                <form
                  onSubmit={handleSubmit}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                >
                  <div className="bg-gray-50 px-4 sm:px-6 py-4 border-b">
                    <h3 className="text-md font-semibold text-gray-700 flex items-center gap-2">
                      Return Information
                    </h3>
                  </div>

                  <div className="p-4 sm:p-6 space-y-6">
                    {/* Reason for Return */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Reason for Return <span className="text-red-500">*</span>
                      </label>
                      <SelectField variant="form"
                        name="reason_for_return"
                        value={formData.reason_for_return}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all bg-white ${errors.reason_for_return ? 'border-red-400' : 'border-gray-300'}`}
                        required
                        disabled={loading}
                      >
                        <option value="">Select a reason for return</option>
                        {returnReasons.map((reason) => (
                          <option key={reason.value} value={reason.value}>
                            {reason.label}
                          </option>
                        ))}
                      </SelectField>
                      <ValidationMessage error={errors.reason_for_return} />
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={!shipmentDetails || !formData.reason_for_return}
                      loading={loading}
                    >
                      {loading ? "Creating Request..." : "Create Return Request"}
                    </Button>
                    <Button variant="secondary" onClick={() => navigate("/returns")} disabled={loading}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
    </DashboardLayout>
  )
}

export default AddReturn;